import { ApolloError, useMutation } from '@apollo/client'
import {
    AddUpdateBestPracticeResponse,
    BestPracticeData,
    RemoveBestPracticeResponse,
} from '@deltek/specification-client/core/Models/bestPractice'
import { summarizeApolloError } from '@deltek/specification-client/core/Utilities/apolloUtils'
import {
    AddUpdateBestPracticeMutation,
    RemoveBestPracticeMutation,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { BP_ERROR_MESSAGES } from '../features/bestPractices/constants'

/**
 * Error details for user-facing notifications
 */
export interface BestPracticeErrorDetails {
    operation: 'addUpdate' | 'remove'
    message: string
    technicalError: string
}

/**
 * Custom hook for Best Practices mutations (add/update and remove)
 *
 * @returns Mutation functions, loading state, and error handler
 */
export const useBestPracticesMutation = () => {
    /**
     * Handles Apollo errors and provides user-facing error messages
     *
     * @param error - Apollo error object
     * @param operation - The operation that failed
     * @returns Error details for notification system
     */
    const handleFetchError = (
        error: ApolloError,
        operation: 'addUpdate' | 'remove'
    ): BestPracticeErrorDetails => {
        const summarizedError = summarizeApolloError(
            error,
            `WorkspaceEditor-${operation}BestPractice`
        )

        // Determine user-facing message
        const userMessage =
            operation === 'addUpdate'
                ? BP_ERROR_MESSAGES.ADD_UPDATE_FAILED
                : BP_ERROR_MESSAGES.REMOVE_FAILED

        return {
            operation,
            message: userMessage,
            technicalError: summarizedError,
        }
    }

    const [
        addUpdateBestPractice,
        { loading: isAddUpdateLoading },
    ] = useMutation<
        AddUpdateBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(AddUpdateBestPracticeMutation, {
        onError: (error) => handleFetchError(error, 'addUpdate'),
    })

    const [removeBestPractice, { loading: isRemoveLoading }] = useMutation<
        RemoveBestPracticeResponse,
        { input: Partial<BestPracticeData> }
    >(RemoveBestPracticeMutation, {
        onError: (error) => handleFetchError(error, 'remove'),
    })

    return {
        addUpdateBestPractice,
        removeBestPractice,
        isLoading: isAddUpdateLoading || isRemoveLoading,
        handleFetchError, // Exposed for manual error handling if needed
    }
}
